import {
  createApi,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';
import {
  BulkCreatePickupCyclePayload,
  BulkCreatePickupCycleResponse,
  CreatePickupCyclePayload,
  PickupBranch,
  PickupCommerceOption,
  PickupCycle,
  PickupCyclesFilter,
  UpdateBranchAddressPayload,
} from '@/pages/Business/pages/PickupCycles/models';

const PAGE_SIZE = 200;

/*
 * Commerces and branches are listed in full: PE has more than 700 commerces
 * and some commerces have over 400 branches, so a single page leaves options
 * out of the modal.
 */
const fetchAllPages = async <T>(
  baseQuery: (
    args: FetchArgs,
  ) => PromiseLike<{ data?: unknown; error?: FetchBaseQueryError }>
    | { data?: unknown; error?: FetchBaseQueryError },
  url: string,
  params: Record<string, string> = {},
): Promise<{ data: T[] } | { error: FetchBaseQueryError }> => {
  const items: T[] = [];
  for (let offset = 0; ; offset += PAGE_SIZE) {
    const result = await baseQuery({
      url,
      params: { ...params, limit: PAGE_SIZE, offset },
    });
    if (result.error) return { error: result.error };
    const page = result.data as PaginatedResponse<T>;
    items.push(...page.items);
    if (page.items.length < PAGE_SIZE || items.length >= page.count) break;
  }
  return { data: items };
};

export type GetPickupCyclesArgs = {
  filters: PickupCyclesFilter;
  paginated: PaginatedRequest;
};

const pickupCyclesService = createApi({
  reducerPath: 'pickupCyclesService',
  baseQuery: createBaseQueryWithReauth(API_BUSINESS_URL),
  tagTypes: ['PickupCycles', 'Branch', 'Branches'] as const,
  endpoints: (builder) => ({
    getPickupCycles: builder.query<
      PaginatedResponse<PickupCycle>,
      GetPickupCyclesArgs
    >({
      query: ({ filters, paginated }) => {
        const params: Record<string, string | number> = {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        };

        if (filters.commerceId && filters.commerceId.trim() !== '') {
          params.commerceId = filters.commerceId.trim();
        }

        if (filters.branchId && filters.branchId.trim() !== '') {
          params.branchId = filters.branchId.trim();
        }

        if (filters.tag && filters.tag.trim() !== '') {
          params.tag = filters.tag.trim();
        }

        if (filters.cycleTime && filters.cycleTime.trim() !== '') {
          params.cycleTime = filters.cycleTime.trim();
        }

        if (filters.days && filters.days.length > 0) {
          params.days = filters.days.join(',');
        }

        return {
          url: '/pickup-cycles',
          params,
        };
      },
      providesTags: ['PickupCycles'],
    }),

    createPickupCycle: builder.mutation<PickupCycle, CreatePickupCyclePayload>({
      query: (body) => ({
        url: '/pickup-cycles',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PickupCycles'],
    }),

    // Alta del mismo retiro para varias sucursales en una sola llamada; el
    // back informa cuáles ya existían en vez de fallar.
    createPickupCyclesBulk: builder.mutation<
      BulkCreatePickupCycleResponse,
      BulkCreatePickupCyclePayload
    >({
      query: (body) => ({
        url: '/pickup-cycles/bulk',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PickupCycles'],
    }),

    deletePickupCycle: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `/pickup-cycles/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PickupCycles'],
    }),

    getPickupCommerces: builder.query<PickupCommerceOption[], void>({
      queryFn: (_arg, _api, _extraOptions, baseQuery) =>
        fetchAllPages<PickupCommerceOption>(baseQuery, '/commerces'),
    }),

    getBranchesByCommerce: builder.query<PickupBranch[], { commerceId: string }>(
      {
        queryFn: ({ commerceId }, _api, _extraOptions, baseQuery) =>
          fetchAllPages<PickupBranch>(baseQuery, '/branches', { commerceId }),
        providesTags: (_result, _error, arg) => [
          { type: 'Branches' as const, id: arg.commerceId },
          'Branches',
        ],
      },
    ),

    getBranchById: builder.query<PickupBranch, { id: string }>({
      query: ({ id }) => ({
        url: `/branches/${id}`,
      }),
      providesTags: (_result, _error, arg) => [{ type: 'Branch', id: arg.id }],
    }),

    updateBranchAddress: builder.mutation<
      PickupBranch,
      { id: string; body: UpdateBranchAddressPayload }
    >({
      query: ({ id, body }) => ({
        url: `/branches/${id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Branch', id: arg.id },
        'Branches',
      ],
    }),
  }),
});

export default pickupCyclesService;

export const {
  useGetPickupCyclesQuery,
  useCreatePickupCycleMutation,
  useCreatePickupCyclesBulkMutation,
  useDeletePickupCycleMutation,
  useGetPickupCommercesQuery,
  useGetBranchesByCommerceQuery,
  useGetBranchByIdQuery,
  useUpdateBranchAddressMutation,
} = pickupCyclesService;
